import { ExtendedInfoType } from "components/types";
import { EncounterDetails, IssuesResponseType } from "entities/issues";
import { InfoTypes, SinglePatientInfo } from "entities/patientInformation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EncounterWidth, TileYearWidth } from "utils/constants";
import { VitalsChartData } from "./types";

import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);

export default function useDayTimeline(
  patientInformations?: SinglePatientInfo[],
  issues?: IssuesResponseType
) {
  const [allDays, setAllDays] = useState<string[]>([]);
  const [maxDaysWidth, setMaxDaysWidth] = useState(0);
  const [maxEncounterWidth, setMaxEncounterWidth] = useState(0);

  const latestYear = useMemo(() => {
    const date = new Date();
    let year = date.getFullYear();

    issues?.encounters?.forEach((data) => {
      const encounterYear = data?.date?.split("-")[0];
      if (encounterYear) {
        year = Math.max(year, +encounterYear);
      }
    });

    return year + 1;
  }, [issues?.encounters]);

  useEffect(() => {
    const obj: Record<string, SinglePatientInfo[]> = {};
    let numberOfItems = 0;
    patientInformations?.forEach((data) => {
      if (data) {
        const splittedDate = data.begdate.split(" ");
        const encounterDay = splittedDate[0];

        if (obj[encounterDay]) {
          obj[encounterDay].push(data);
        } else {
          obj[encounterDay] = [data];
        }
        numberOfItems++;
      }
    });

    const distinctDays = Object.keys(obj);

    setMaxDaysWidth(numberOfItems * EncounterWidth + distinctDays.length * 200);
  }, [patientInformations]);

  const getDaysBetween = useCallback((start: string, end: string): string[] => {
    const startDate = dayjs(start);
    const endDate = dayjs(end);
    const days = [];

    let currentDate = startDate.clone();

    while (currentDate.isSameOrBefore(endDate, "day")) {
      const formattedDay = currentDate.format("YYYY-MM-DD");
      days.push(formattedDay);
      currentDate = currentDate.add(1, "day");
    }

    return days;
  }, []);

  const groupPatientData = useMemo(() => {
    const groupedData: Record<InfoTypes, ExtendedInfoType[]> = {
      allergy: [],
      dental: [],
      medical_problem: [],
      medication: [],
      surgery: [],
      tests: [],
      vitals: [],
      medical_device: [],
    };
    const date = new Date(`${latestYear}`);
    const dateString = date.toISOString();

    const fullDate = `${dateString.split("T")[0]}`;

    const newMonths = new Set(allDays);

    if (patientInformations) {
      patientInformations.forEach((info) => {
        const beginingDate = info?.begdate.split(" ")[0];
        let endDate = info.enddate ? info.enddate.split(" ")[0] : fullDate;

        if (
          info.type === "surgery" ||
          info.type === "allergy" ||
          info.type === "medical_device" ||
          info.type === "dental"
        ) {
          endDate = fullDate;
        }

        newMonths.add(beginingDate);
        newMonths.add(endDate);
        const days = getDaysBetween(beginingDate, endDate);

        groupedData[info.type].push({
          ...info,
          enddate: info.enddate ? info.enddate.split(" ")[0] : fullDate,
          tileWidth: TileYearWidth,
          end: endDate,
          start: beginingDate,
          contigiousDates: days,
        });
      });
    }

    setAllDays(Array.from(newMonths).sort());

    return groupedData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientInformations, latestYear, getDaysBetween]);

  const daysEncounter = useMemo(() => {
    const encounters: Record<string, EncounterDetails[]> = {};
    const vitalChartData: VitalsChartData = {};
    allDays.forEach((day) => {
      if (day !== "NaN") {
        encounters[day] = [];
        vitalChartData[day] = [];
      }
    });

    if (issues) {
      issues.encounters.forEach((encounter, i) => {
        const day = encounter.date.split(" ")[0];
        if (day !== "0000-00-00") {
          encounters[day] = [...encounter.encounters];

          encounter.encounters.forEach((data) => {
            data.vitals.forEach((vitals) => {
              if (vitalChartData[day]) {
                vitalChartData[day].push({
                  bps: vitals.bps,
                  bpd: vitals.bpd,
                  pulse: vitals.pulse,
                  temperature: vitals.temperature,
                  height: vitals.height,
                  respiratoryRate: vitals.respiration,
                  weight: vitals.weight,
                  bmi: vitals.BMI,
                  date: vitals.date,
                });
              } else {
                vitalChartData[day] = [
                  {
                    bps: vitals.bps,
                    bpd: vitals.bpd,
                    pulse: vitals.pulse,
                    temperature: vitals.temperature,
                    height: vitals.height,
                    respiratoryRate: vitals.respiration,
                    weight: vitals.weight,
                    bmi: vitals.BMI,
                    date: vitals.date,
                  },
                ];
              }
            });
          });
        }
      });
    }

    return { encounters, vitalChartData };
  }, [issues, allDays]);

  useEffect(() => {
    const dayEncounterKeys = Object.keys(daysEncounter.encounters);
    let width = 0;
    for (const day of dayEncounterKeys) {
      if (daysEncounter.encounters[day as InfoTypes]) {
        width +=
          daysEncounter.encounters[day as InfoTypes].length * EncounterWidth +
          300;
      } else {
        width += TileYearWidth;
      }
    }
    setMaxEncounterWidth(width);
  }, [daysEncounter.encounters]);

  return {
    days: Object.keys(daysEncounter.encounters).sort(),
    maxDaysWidth,
    dayGroupPatientData: groupPatientData,
    daysEncounter: daysEncounter.encounters,
    vitalsChartData: daysEncounter.vitalChartData,
    maxDaysEncounterWidth: maxEncounterWidth,
  };
}
